import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../common/user";
import { agentsService } from "../../../services/admin/cashier.service";
import { OrderType } from "../../../components/affiliate/Trackers";
import { setGeneralProgressHide, setGeneralProgressShow, setGeneralSnackbarState } from "../../common/ui";


export type payloadAgentsList = {
  page: number;
  perPage?: number;
  order: OrderType;
  filter: {
    id?: number | string | null;
    underAgentId?: number | null;
    level?: number | null;
    search?: string | null;
    regDateFrom?: string | null;
    regDateTo?: string | null;
    status?: string[] | number[] | null;
    type?: string[] | number[] | null;
    country?: string[] | number[] | null;
    shouldExcludeSelf?: boolean;
    platformIds?: string[] | number[] | null;
  }
};

export type payloadPlayersList = {
  page: number;
  perPage?: number;
  order: OrderType;
  filter: {
    search?: string | null;
    regDateFrom?: string | null;
    regDateTo?: string | null;
    status?: string[] | number[] | null;
    country?: string[] | number[] | null;
  }
};

export type payloadDescendantAgents = {
  page: number;
  perPage?: number;
  search: string | null;
  filter?: {
    underAgentId?: number | null,
    level?: number | null,
    search?:  string | null,
    regDateFrom?: string | null,
    regDateTo?: string | null,
    status?: string[] | null,
    type?: string[] | null,
    country?: string[] | null,
    shouldExcludeSelf?: boolean,
    platformIds?: number[] | string[] | null,
  },
};

export type payloadCashierAdmin = {
  page: number;
  perPage?: number;
  order: OrderType;
  filter?: {
    search: string | null;
    regDateFrom?: string | null,
    regDateTo?: string | null,
    platformIds?: number[] | string[] | null,
  },
};

export type payloadPlayersOptions = {
  page: number;
  perPage?: number;
  search: string | null;
  filter?: {
    type?: string[] | null,
  },
};

export type payloadRoles = {
  type: 'agent' | 'master-agent';
};

export type payloadWalletIdentities = {
  agentId: number;
};

export type payloadPlayerWalletIdentities = {
  playerId: number;
};

export type payloadAgentProfile = {
  agentId: number | string;
};

export type payloadPlayerProfile = {
  playerId: number | string;
};

type countriesItem = {
  id: string,
  label: string,
}

export type agentsListItem = {
  id: number;
  username: string;
  status: string;
  type: string;
  email: string;
  countries: countriesItem[];
  createdAt: string;
  level: number;
  allSubAgents: number;
  directSubAgents: number;
  playersCount: number;
  role: string;
  lastVisitCountry: any;
  lastVisitIp: string | null;
  lastVisitTime: string;
};

export type agentsColumnsItem = {
  name: string;
  type: string;
  sort: boolean;
  match?: boolean;
};

export interface ITableData {
  list: agentsListItem[];
  columns: agentsColumnsItem[];
  startRowNum: number;
  endRowNum: number;
  totalEntries: number | string;
  totalPages: number;
}

export type WalletItemType = {
  operator: string;
  brand: string;
  currency: string;
}

export type BrandItemType = {
  code: string;
  countries: string[];
  currencies: string[];
  description: string;
  domain: string;
  name: string;
  operator: string;
}

export type payloadStartBranch = {
  wallets: WalletItemType[];
};

export type payloadUpdateAgentProfile = {
  agentId: number | string;
  payload: {
    [key: string]: any;
  }
}

export type payloadUpdateAgentRole = {
  agentId: number | string;
  payload: {
    [key: string]: any;
  }
}

export type agentsSliceState = {
  list: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  fullList: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  details: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  fullDetails: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  wallets: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  enableWallet: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  disableWallet: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  addWallet: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  brandsList: {
    data: BrandItemType[] | null,
    error: any,
    isFetching: boolean,
  },
  createWallets: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  descendantAgents: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  fullAgentsOptions: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  fullTrAgentsOptions: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  agentRoles: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  walletIdentities: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  cashierWalletIdentities: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  createAgent: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  agentBalance: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  adminBalance: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  playerList: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  fullPlayerList: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  playerDetails: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  fullPlayerDetails: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  playerBalance: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  transferToAgent: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  transferAllToAgent: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  playersOptions: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  playerWalletIdentities: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  transferToPlayer: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  agentProfile: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  updateAgentProfile: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  updateAgentRole: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  playerProfile: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  externalPlayerProfile: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  filterRoles: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  filterRolesAll: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  blockAgent: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  agentNewPassword: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  betListFilters: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  casinoBetListFilters: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  blockPlayer: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  playerStatus: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  playerNewPassword: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  createPlayer: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  cashierAdminOptions: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
};

const initialState: agentsSliceState = {
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
  fullList: {
    data: null,
    error: null,
    isFetching: false,
  },
  details: {
    data: null,
    error: null,
    isFetching: false,
  },
  fullDetails: {
    data: null,
    error: null,
    isFetching: false,
  },
  wallets: {
    data: null,
    error: null,
    isFetching: false,
  },
  enableWallet: {
    data: null,
    error: null,
    isFetching: false,
  },
  disableWallet: {
    data: null,
    error: null,
    isFetching: false,
  },
  addWallet: {
    data: null,
    error: null,
    isFetching: false,
  },
  brandsList: {
    data: null,
    error: null,
    isFetching: false,
  },
  createWallets: {
    data: null,
    error: null,
    isFetching: false,
  },
  descendantAgents: {
    data: null,
    error: null,
    isFetching: false,
  },
  fullAgentsOptions: {
    data: null,
    error: null,
    isFetching: false,
  },
  fullTrAgentsOptions: {
    data: null,
    error: null,
    isFetching: false,
  },
  agentRoles: {
    data: null,
    error: null,
    isFetching: false,
  },
  walletIdentities: {
    data: null,
    error: null,
    isFetching: false,
  },
  cashierWalletIdentities: {
    data: null,
    error: null,
    isFetching: false,
  },
  createAgent: {
    data: null,
    error: null,
    isFetching: false,
  },
  agentBalance: {
    data: null,
    error: null,
    isFetching: false,
  },
  adminBalance: {
    data: null,
    error: null,
    isFetching: false,
  },
  playerList: {
    data: null,
    error: null,
    isFetching: false,
  },
  fullPlayerList: {
    data: null,
    error: null,
    isFetching: false,
  },
  playerDetails: {
    data: null,
    error: null,
    isFetching: false,
  },
  fullPlayerDetails: {
    data: null,
    error: null,
    isFetching: false,
  },
  playerBalance: {
    data: null,
    error: null,
    isFetching: false,
  },
  transferToAgent: {
    data: null,
    error: null,
    isFetching: false,
  },
  transferAllToAgent: {
    data: null,
    error: null,
    isFetching: false,
  },
  playersOptions: {
    data: null,
    error: null,
    isFetching: false,
  },
  playerWalletIdentities: {
    data: null,
    error: null,
    isFetching: false,
  },
  transferToPlayer: {
    data: null,
    error: null,
    isFetching: false,
  },
  agentProfile: {
    data: null,
    error: null,
    isFetching: false,
  },
  updateAgentProfile: {
    data: null,
    error: null,
    isFetching: false,
  },
  updateAgentRole: {
    data: null,
    error: null,
    isFetching: false,
  },
  playerProfile: {
    data: null,
    error: null,
    isFetching: false,
  },
  externalPlayerProfile: {
    data: null,
    error: null,
    isFetching: false,
  },
  filterRoles: {
    data: null,
    error: null,
    isFetching: false,
  },
  filterRolesAll: {
    data: null,
    error: null,
    isFetching: false,
  },
  blockAgent: {
    data: null,
    error: null,
    isFetching: false,
  },
  agentNewPassword: {
    data: null,
    error: null,
    isFetching: false,
  },
  betListFilters: {
    data: null,
    error: null,
    isFetching: false,
  },
  casinoBetListFilters: {
    data: null,
    error: null,
    isFetching: false,
  },
  blockPlayer: {
    data: null,
    error: null,
    isFetching: false,
  },
  playerStatus: {
    data: null,
    error: null,
    isFetching: false,
  },
  playerNewPassword: {
    data: null,
    error: null,
    isFetching: false,
  },
  createPlayer: {
    data: null,
    error: null,
    isFetching: false,
  },
  cashierAdminOptions: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getListData: any = createAsyncThunk(
  'cashier/getListData',
  async (payload: payloadAgentsList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getFullListData: any = createAsyncThunk(
  'cashier/getFullListData',
  async (payload: payloadAgentsList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getFullList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getDetailsListData: any = createAsyncThunk(
  'cashier/getDetailsListData',
  async (payload: payloadAgentsList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getFullDetailsListData: any = createAsyncThunk(
  'cashier/getFullDetailsListData',
  async (payload: payloadAgentsList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getFullList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getWalletsData: any = createAsyncThunk(
  'cashier/getWalletsData',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getWallets();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getBrandsListData: any = createAsyncThunk(
  'cashier/getBrandsListData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getBrandsList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const createWalletsData: any = createAsyncThunk(
  'cashier/createWalletsData',
  async (payload: payloadStartBranch, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.createBranch(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getDescendantAgentsData: any = createAsyncThunk(
  'cashier/getDescendantAgentsData',
  async (payload: payloadDescendantAgents, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getDescendantAgents(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getFullAgentsOptionData: any = createAsyncThunk(
  'cashier/getFullAgentsOptionData',
  async (payload: payloadDescendantAgents, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getFullAgentsOptions(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getFullTrAgentsOptionData: any = createAsyncThunk(
  'cashier/getFullTrAgentsOptionData',
  async (payload: payloadDescendantAgents, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getFullAgentsOptions(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getAgentRolesData: any = createAsyncThunk(
  'cashier/getAgentRolesData',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getRoles();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);
export const getWalletIdentitiesData: any = createAsyncThunk(
  'cashier/getWalletIdentitiesData',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getWalletIdentities();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data?.options;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const createAgentData: any = createAsyncThunk(
  'cashier/createAgentData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.createAgent(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok && response.status === 201) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.created',
          })
        );
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      )
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const enableWalletData: any = createAsyncThunk(
  'cashier/enableWalletData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.enableWallet(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok && response.status === 200) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.wallet_activated',
          })
        );
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        )
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return { ...data, ...payload };
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      )
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const disableWalletData: any = createAsyncThunk(
  'cashier/disableWalletData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.disableWallet(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok && response.status === 200) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.wallet_deactivated',
          })
        );
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: !!data?.error ? data?.error : 'common.messages.error',
          })
        )
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return { ...data, ...payload };
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      )
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const addWalletsData: any = createAsyncThunk(
  'cashier/addWalletsData',
  async (payload: WalletItemType, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.addWallet(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getAgentBalanceByIdData: any = createAsyncThunk(
  'cashier/getAgentBalanceByIdData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getAgentBalanceById(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.errors.agent.balance.unable_to_retrieve_balance',
          })
        )
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return {
        ...data,
        agentId: payload.agentId,
        payload: payload.payload,
      };
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getAdminBalanceUpdateData: any = createAsyncThunk(
  'cashier/getAdminBalanceUpdateData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getAdminBalanceUpdate(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.errors.agent.balance.unable_to_retrieve_balance',
          })
        )
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return {
        ...data,
        agentId: payload.agentId,
        payload: payload.payload,
      };
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getPlayerListData: any = createAsyncThunk(
  'admin/getPlayerListData',
  async (payload: payloadPlayersList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getPlayerList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getPlayerListDetailsData: any = createAsyncThunk(
  'admin/getPlayerListDetailsData',
  async (payload: payloadPlayersList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getPlayerList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getFullPlayerListData: any = createAsyncThunk(
  'admin/getFullPlayerListData',
  async (payload: payloadPlayersList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getFullPlayerList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getFullPlayerListDetailsData: any = createAsyncThunk(
  'admin/getFullPlayerListDetailsData',
  async (payload: payloadPlayersList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getFullPlayerList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getPlayerBalanceByIdData: any = createAsyncThunk(
  'admin/getPlayerBalanceByIdData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getPlayerBalanceById(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.errors.agent.balance.unable_to_retrieve_balance',
          })
        )
        return rejectWithValue(
          {
            ...data,
            playerId: payload.playerId,
          }
        )
      }

      dispatch(setGeneralProgressHide());

      return {
        ...data,
        playerId: payload.playerId,
      };
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue({
        error: error,
        playerId: payload.playerId,
      })
    }
  }
);

export const transferToAgentData: any = createAsyncThunk(
  'admin/transferToAgentData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.setTransferToAgent(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const transferAllToAgentData: any = createAsyncThunk(
  'admin/transferAllToAgentData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.setTransferAllToAgent(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getPlayersOptionsData: any = createAsyncThunk(
  'cashier/getPlayersOptionsData',
  async (payload: payloadPlayersOptions, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getPlayersOptions(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getPlayerWalletIdentitiesData: any = createAsyncThunk(
  'cashier/getPlayerWalletIdentitiesData',
  async (payload: payloadPlayerWalletIdentities, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getPlayerWalletIdentities(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data?.walletIdentities;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const transferToPlayerData: any = createAsyncThunk(
  'admin/transferToPlayerData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.setTransferToPlayer(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getAgentProfileData: any = createAsyncThunk(
  'cashier/getAgentProfileData',
  async (payload: payloadAgentProfile, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getAgentProfile(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const updateAgentProfileData: any = createAsyncThunk(
  'cashier/updateAgentProfileData',
  async (payload: payloadUpdateAgentProfile, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.updateAgentProfile(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getFilterRolesData: any = createAsyncThunk(
  'cashier/getFilterRolesData',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getFilterRoles();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getFilterRolesAllData: any = createAsyncThunk(
  'cashier/getFilterRolesAllData',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getFilterRolesAll();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const updateAgentRoleData: any = createAsyncThunk(
  'cashier/updateAgentRoleData',
  async (payload: payloadUpdateAgentRole, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.updateAgentRole(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getPlayerProfileData: any = createAsyncThunk(
  'cashier/getPlayerProfileData',
  async (payload: payloadPlayerProfile, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getPlayerProfile(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getExternalPlayerProfileData: any = createAsyncThunk(
  'cashier/getExternalPlayerProfileData',
  async (payload: payloadPlayerProfile, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getExternalPlayerProfile(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const toggleBlockAgentData: any = createAsyncThunk(
  'cashier/toggleBlockAgentData',
  async (payload: payloadUpdateAgentProfile, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.toggleBlockAgent(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const setAgentNewPasswordData: any = createAsyncThunk(
  'cashier/setAgentNewPasswordData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.setAgentNewPassword(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getBetListFiltersData: any = createAsyncThunk(
  'cashier/getBetListFiltersData',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getBetListFilters();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getCasinoBetListFiltersData: any = createAsyncThunk(
  'cashier/getCasinoBetListFiltersData',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getCasinoBetListFilters();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const toggleBlockPlayerData: any = createAsyncThunk(
  'cashier/toggleBlockPlayerData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.toggleBlockPlayer(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        )
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      )
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getPlayerStatusData: any = createAsyncThunk(
  'cashier/getPlayerStatusData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getPlayerStatus(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const setPlayerNewPasswordData: any = createAsyncThunk(
  'cashier/setPlayerNewPasswordData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.setPlayerNewPassword(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const createPlayerData: any = createAsyncThunk(
  'cashier/createPlayerData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.createPlayer(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok && response.status === 201) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.created',
          })
        );
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        )
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      )
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getCashierWalletIdentitiesData: any = createAsyncThunk(
  'cashier/getCashierWalletIdentitiesData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getCashierWalletIdentities(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data?.walletIdentities;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getCashierAdminOptionsData: any = createAsyncThunk(
  'cashier/getCashierAdminOptionsData',
  async (payload: payloadCashierAdmin, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentsService.getCashierAdminOptions(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);


export const agentsSlice = createSlice({
  name: 'agents',
  initialState: initialState,
  reducers: {
    clearListState(state) {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = null;
    },
    clearFullListState(state) {
      state.fullList.error = null;
      state.fullList.isFetching = false;
      state.fullList.data = null;
    },
    clearDetailsState(state) {
      state.details.error = null;
      state.details.isFetching = false;
      state.details.data = null;
    },
    clearFullDetailsState(state) {
      state.fullDetails.error = null;
      state.fullDetails.isFetching = false;
      state.fullDetails.data = null;
    },
    clearBrandsListState(state) {
      state.brandsList.error = null;
      state.brandsList.isFetching = false;
      state.brandsList.data = null;
    },
    clearWalletsState(state) {
      state.wallets.error = null;
      state.wallets.isFetching = false;
      state.wallets.data = null;
    },
    clearCreateWalletsState(state) {
      state.createWallets.error = null;
      state.createWallets.isFetching = false;
      state.createWallets.data = null;
    },
    clearDescendantAgentsState(state) {
      state.descendantAgents.error = null;
      state.descendantAgents.isFetching = false;
      state.descendantAgents.data = null;
    },
    clearFullAgentsOptionsState(state) {
      state.fullAgentsOptions.error = null;
      state.fullAgentsOptions.isFetching = false;
      state.fullAgentsOptions.data = null;
    },
    clearFullTrAgentsOptionsState(state) {
      state.fullTrAgentsOptions.error = null;
      state.fullTrAgentsOptions.isFetching = false;
      state.fullTrAgentsOptions.data = null;
    },
    clearAgentRolesState(state) {
      state.agentRoles.error = null;
      state.agentRoles.isFetching = false;
      state.agentRoles.data = null;
    },
    clearWalletIdentitiesState(state) {
      state.walletIdentities.error = null;
      state.walletIdentities.isFetching = false;
      state.walletIdentities.data = null;
    },
    clearCreateAgentState(state) {
      state.createAgent.error = null;
      state.createAgent.isFetching = false;
      state.createAgent.data = null;
    },
    clearEnableWalletState(state) {
      state.enableWallet.error = null;
      state.enableWallet.isFetching = false;
      state.enableWallet.data = null;
    },
    clearDisableWalletState(state) {
      state.disableWallet.error = null;
      state.disableWallet.isFetching = false;
      state.disableWallet.data = null;
    },
    clearAddWalletState(state) {
      state.addWallet.error = null;
      state.addWallet.isFetching = false;
      state.addWallet.data = null;
    },
    clearAgentBalanceState(state) {
      state.agentBalance.error = null;
      state.agentBalance.isFetching = false;
      state.agentBalance.data = null;
    },
    clearPlayerListState(state) {
      state.playerList.error = null;
      state.playerList.isFetching = false;
      state.playerList.data = null;
    },
    clearFullPlayerListState(state) {
      state.fullPlayerList.error = null;
      state.fullPlayerList.isFetching = false;
      state.fullPlayerList.data = null;
    },
    clearPlayerBalanceState(state) {
      state.playerBalance.error = null;
      state.playerBalance.isFetching = false;
      state.playerBalance.data = null;
    },
    clearPlayerListDetailsState(state) {
      state.playerDetails.error = null;
      state.playerDetails.isFetching = false;
      state.playerDetails.data = null;
    },
    clearFullPlayerListDetailsState(state) {
      state.fullPlayerDetails.error = null;
      state.fullPlayerDetails.isFetching = false;
      state.fullPlayerDetails.data = null;
    },
    clearTransferToAgentState(state) {
      state.transferToAgent.error = null;
      state.transferToAgent.isFetching = false;
      state.transferToAgent.data = null;
    },
    clearTransferAllToAgentState(state) {
      state.transferAllToAgent.error = null;
      state.transferAllToAgent.isFetching = false;
      state.transferAllToAgent.data = null;
    },
    clearPlayersOptionsState(state) {
      state.playersOptions.error = null;
      state.playersOptions.isFetching = false;
      state.playersOptions.data = null;
    },
    clearPlayerWalletIdentitiesState(state) {
      state.playerWalletIdentities.error = null;
      state.playerWalletIdentities.isFetching = false;
      state.playerWalletIdentities.data = null;
    },
    clearTransferToPlayerState(state) {
      state.transferToPlayer.error = null;
      state.transferToPlayer.isFetching = false;
      state.transferToPlayer.data = null;
    },
    clearUpdateAgentProfileState(state) {
      state.updateAgentProfile.error = null;
      state.updateAgentProfile.isFetching = false;
      state.updateAgentProfile.data = null;
    },
    clearAgentProfileState(state) {
      state.agentProfile.error = null;
      state.agentProfile.isFetching = false;
      state.agentProfile.data = null;
    },
    clearAgentRoleState(state) {
      state.updateAgentRole.error = null;
      state.updateAgentRole.isFetching = false;
      state.updateAgentRole.data = null;
    },
    clearPlayerProfileState(state) {
      state.playerProfile.error = null;
      state.playerProfile.isFetching = false;
      state.playerProfile.data = null;
    },
    clearExternalPlayerProfileState(state) {
      state.externalPlayerProfile.error = null;
      state.externalPlayerProfile.isFetching = false;
      state.externalPlayerProfile.data = null;
    },
    clearToggleBlockAgentState(state) {
      state.blockAgent.error = null;
      state.blockAgent.isFetching = false;
      state.blockAgent.data = null;
    },
    clearAgentNewPasswordState(state) {
      state.agentNewPassword.error = null;
      state.agentNewPassword.isFetching = false;
      state.agentNewPassword.data = null;
    },
    clearToggleBlockPlayerState(state) {
      state.blockPlayer.error = null;
      state.blockPlayer.isFetching = false;
      state.blockPlayer.data = null;
    },
    clearPlayerStatusState(state) {
      state.playerStatus.error = null;
      state.playerStatus.isFetching = false;
      state.playerStatus.data = null;
    },
    clearPlayerNewPasswordState(state) {
      state.playerNewPassword.error = null;
      state.playerNewPassword.isFetching = false;
      state.playerNewPassword.data = null;
    },
    clearCreatePlayerState(state) {
      state.createPlayer.error = null;
      state.createPlayer.isFetching = false;
      state.createPlayer.data = null;
    },
    clearAdminBalanceState(state) {
      state.adminBalance.error = null;
      state.adminBalance.isFetching = false;
      state.adminBalance.data = null;
    },
    clearCashierWalletIdentitiesState(state) {
      state.cashierWalletIdentities.error = null;
      state.cashierWalletIdentities.isFetching = false;
      state.cashierWalletIdentities.data = null;
    },
    clearCashierAdminOptionsState(state) {
      state.cashierAdminOptions.error = null;
      state.cashierAdminOptions.isFetching = false;
      state.cashierAdminOptions.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<agentsSliceState>>) => {
    builder.addCase(getListData.pending, (state ) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getListData.fulfilled, (state , action) => {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = action.payload;
    });
    builder.addCase(getListData.rejected, (state , action) => {
      state.list.isFetching = false;
      state.list.error = action.payload;
      state.list.data = null;
    });

    builder.addCase(getFullListData.pending, (state ) => {
      state.fullList.isFetching = true;
      state.fullList.error = null;
    });
    builder.addCase(getFullListData.fulfilled, (state , action) => {
      state.fullList.error = null;
      state.fullList.isFetching = false;
      state.fullList.data = action.payload;
    });
    builder.addCase(getFullListData.rejected, (state , action) => {
      state.fullList.isFetching = false;
      state.fullList.error = action.payload;
      state.fullList.data = null;
    });

    builder.addCase(getDetailsListData.pending, (state ) => {
      state.details.isFetching = true;
      state.details.error = null;
    });
    builder.addCase(getDetailsListData.fulfilled, (state , action) => {
      state.details.error = null;
      state.details.isFetching = false;
      state.details.data = action.payload;
    });
    builder.addCase(getDetailsListData.rejected, (state , action) => {
      state.details.isFetching = false;
      state.details.error = action.payload;
      state.details.data = null;
    });

    builder.addCase(getFullDetailsListData.pending, (state ) => {
      state.fullDetails.isFetching = true;
      state.fullDetails.error = null;
    });
    builder.addCase(getFullDetailsListData.fulfilled, (state , action) => {
      state.fullDetails.error = null;
      state.fullDetails.isFetching = false;
      state.fullDetails.data = action.payload;
    });
    builder.addCase(getFullDetailsListData.rejected, (state , action) => {
      state.fullDetails.isFetching = false;
      state.fullDetails.error = action.payload;
      state.fullDetails.data = null;
    });

    builder.addCase(getBrandsListData.pending, (state ) => {
      state.brandsList.isFetching = true;
      state.brandsList.error = null;
    });
    builder.addCase(getBrandsListData.fulfilled, (state , action) => {
      state.brandsList.error = null;
      state.brandsList.isFetching = false;
      state.brandsList.data = action.payload;
    });
    builder.addCase(getBrandsListData.rejected, (state , action) => {
      state.brandsList.isFetching = false;
      state.brandsList.error = action.payload;
      state.brandsList.data = null;
    });

    builder.addCase(getWalletsData.pending, (state ) => {
      state.wallets.isFetching = true;
      state.wallets.error = null;
    });
    builder.addCase(getWalletsData.fulfilled, (state , action) => {
      state.wallets.error = null;
      state.wallets.isFetching = false;
      state.wallets.data = action.payload;
    });
    builder.addCase(getWalletsData.rejected, (state , action) => {
      state.wallets.isFetching = false;
      state.wallets.error = action.payload;
      state.wallets.data = null;
    });

    builder.addCase(createWalletsData.pending, (state ) => {
      state.createWallets.isFetching = true;
      state.createWallets.error = null;
    });
    builder.addCase(createWalletsData.fulfilled, (state , action) => {
      state.createWallets.error = null;
      state.createWallets.isFetching = false;
      state.createWallets.data = action.payload;
    });
    builder.addCase(createWalletsData.rejected, (state , action) => {
      state.createWallets.isFetching = false;
      state.createWallets.error = action.payload;
      state.createWallets.data = null;
    });

    builder.addCase(getDescendantAgentsData.pending, (state ) => {
      state.descendantAgents.isFetching = true;
      state.descendantAgents.error = null;
    });
    builder.addCase(getDescendantAgentsData.fulfilled, (state , action) => {
      state.descendantAgents.error = null;
      state.descendantAgents.isFetching = false;
      state.descendantAgents.data = action.payload;
    });
    builder.addCase(getDescendantAgentsData.rejected, (state , action) => {
      state.descendantAgents.isFetching = false;
      state.descendantAgents.error = action.payload;
      state.descendantAgents.data = null;
    });

    builder.addCase(getFullAgentsOptionData.pending, (state ) => {
      state.fullAgentsOptions.isFetching = true;
      state.fullAgentsOptions.error = null;
    });
    builder.addCase(getFullAgentsOptionData.fulfilled, (state , action) => {
      state.fullAgentsOptions.error = null;
      state.fullAgentsOptions.isFetching = false;
      state.fullAgentsOptions.data = action.payload;
    });
    builder.addCase(getFullAgentsOptionData.rejected, (state , action) => {
      state.fullAgentsOptions.isFetching = false;
      state.fullAgentsOptions.error = action.payload;
      state.fullAgentsOptions.data = null;
    });

    builder.addCase(getFullTrAgentsOptionData.pending, (state ) => {
      state.fullTrAgentsOptions.isFetching = true;
      state.fullTrAgentsOptions.error = null;
    });
    builder.addCase(getFullTrAgentsOptionData.fulfilled, (state , action) => {
      state.fullTrAgentsOptions.error = null;
      state.fullTrAgentsOptions.isFetching = false;
      state.fullTrAgentsOptions.data = action.payload;
    });
    builder.addCase(getFullTrAgentsOptionData.rejected, (state , action) => {
      state.fullTrAgentsOptions.isFetching = false;
      state.fullTrAgentsOptions.error = action.payload;
      state.fullTrAgentsOptions.data = null;
    });

    builder.addCase(getAgentRolesData.pending, (state ) => {
      state.agentRoles.isFetching = true;
      state.agentRoles.error = null;
    });
    builder.addCase(getAgentRolesData.fulfilled, (state , action) => {
      state.agentRoles.error = null;
      state.agentRoles.isFetching = false;
      state.agentRoles.data = action.payload;
    });
    builder.addCase(getAgentRolesData.rejected, (state , action) => {
      state.agentRoles.isFetching = false;
      state.agentRoles.error = action.payload;
      state.agentRoles.data = null;
    });

    builder.addCase(getWalletIdentitiesData.pending, (state ) => {
      state.walletIdentities.isFetching = true;
      state.walletIdentities.error = null;
    });
    builder.addCase(getWalletIdentitiesData.fulfilled, (state , action) => {
      state.walletIdentities.error = null;
      state.walletIdentities.isFetching = false;
      state.walletIdentities.data = action.payload;
    });
    builder.addCase(getWalletIdentitiesData.rejected, (state , action) => {
      state.walletIdentities.isFetching = false;
      state.walletIdentities.error = action.payload;
      state.walletIdentities.data = null;
    });

    builder.addCase(createAgentData.pending, (state ) => {
      state.createAgent.isFetching = true;
      state.createAgent.error = null;
    });
    builder.addCase(createAgentData.fulfilled, (state , action) => {
      state.createAgent.error = null;
      state.createAgent.isFetching = false;
      state.createAgent.data = action.payload;
    });
    builder.addCase(createAgentData.rejected, (state , action) => {
      state.createAgent.isFetching = false;
      state.createAgent.error = action.payload;
      state.createAgent.data = null;
    });

    builder.addCase(enableWalletData.pending, (state ) => {
      state.enableWallet.isFetching = true;
      state.enableWallet.error = null;
    });
    builder.addCase(enableWalletData.fulfilled, (state , action) => {
      state.enableWallet.error = null;
      state.enableWallet.isFetching = false;
      state.enableWallet.data = action.payload;
    });
    builder.addCase(enableWalletData.rejected, (state , action) => {
      state.enableWallet.isFetching = false;
      state.enableWallet.error = action.payload;
      state.enableWallet.data = null;
    });

    builder.addCase(disableWalletData.pending, (state ) => {
      state.disableWallet.isFetching = true;
      state.disableWallet.error = null;
    });
    builder.addCase(disableWalletData.fulfilled, (state , action) => {
      state.disableWallet.error = null;
      state.disableWallet.isFetching = false;
      state.disableWallet.data = action.payload;
    });
    builder.addCase(disableWalletData.rejected, (state , action) => {
      state.disableWallet.isFetching = false;
      state.disableWallet.error = action.payload;
      state.disableWallet.data = null;
    });

    builder.addCase(addWalletsData.pending, (state ) => {
      state.addWallet.isFetching = true;
      state.addWallet.error = null;
    });
    builder.addCase(addWalletsData.fulfilled, (state , action) => {
      state.addWallet.error = null;
      state.addWallet.isFetching = false;
      state.addWallet.data = action.payload;
    });
    builder.addCase(addWalletsData.rejected, (state , action) => {
      state.addWallet.isFetching = false;
      state.addWallet.error = action.payload;
      state.addWallet.data = null;
    });
    builder.addCase(getAgentBalanceByIdData.pending, (state ) => {
      state.agentBalance.isFetching = true;
      state.agentBalance.error = null;
    });
    builder.addCase(getAgentBalanceByIdData.fulfilled, (state , action) => {
      state.agentBalance.error = null;
      state.agentBalance.isFetching = false;
      state.agentBalance.data = {
        ...action.payload,
        wallet: action.meta.arg.payload,
      };

      const payload = action.meta.arg.payload;
      const updatedBalance = action.payload.balance;

      if (state.wallets.data && Array.isArray(state.wallets.data.wallets)) {
        state.wallets.data = {
          ...state.wallets.data,
          wallets: state.wallets.data.wallets.map((wallet: any) => {
            const agentId = JSON.parse(JSON.stringify(state.wallets.data)).adminId;
            if (
              wallet.operator === payload.operator &&
              wallet.brand === payload.brand &&
              wallet.currency === payload.currency &&
              action.payload === agentId
            ) {
              return {
                ...wallet,
                balance: updatedBalance,
              };
            }
            return wallet;
          }),
        };
      }
    });
    builder.addCase(getAgentBalanceByIdData.rejected, (state , action) => {
      state.agentBalance.isFetching = false;
      state.agentBalance.error = action.payload;
      state.agentBalance.data = null;
    });

    builder.addCase(getAdminBalanceUpdateData.pending, (state ) => {
      state.adminBalance.isFetching = true;
      state.adminBalance.error = null;
    });
    builder.addCase(getAdminBalanceUpdateData.fulfilled, (state , action) => {
      state.adminBalance.error = null;
      state.adminBalance.isFetching = false;
      state.adminBalance.data = {
        ...action.payload,
        wallet: action.meta.arg.payload,
      };

      const payload = action.meta.arg.payload;
      const updatedBalance = action.payload.balance;

      if (state.wallets.data && Array.isArray(state.wallets.data.wallets)) {
        state.wallets.data = {
          ...state.wallets.data,
          wallets: state.wallets.data.wallets.map((wallet: any) => {
            const agentId = JSON.parse(JSON.stringify(state.wallets.data)).adminId;
            if (
              wallet.operator === payload.operator &&
              wallet.brand === payload.brand &&
              wallet.currency === payload.currency &&
              action.payload === agentId
            ) {
              return {
                ...wallet,
                balance: updatedBalance,
              };
            }
            return wallet;
          }),
        };
      }
    });
    builder.addCase(getAdminBalanceUpdateData.rejected, (state , action) => {
      state.adminBalance.isFetching = false;
      state.adminBalance.error = action.payload;
      state.adminBalance.data = null;
    });

    builder.addCase(getPlayerListData.pending, (state) => {
      state.playerList.isFetching = true;
      state.playerList.error = null;
    });
    builder.addCase(getPlayerListData.fulfilled, (state, action) => {
      state.playerList.error = null;
      state.playerList.isFetching = false;
      state.playerList.data = action.payload;
    });
    builder.addCase(getPlayerListData.rejected, (state, action) => {
      state.playerList.isFetching = false;
      state.playerList.error = action.payload;
      state.playerList.data = null;
    });

    builder.addCase(getFullPlayerListData.pending, (state) => {
      state.fullPlayerList.isFetching = true;
      state.fullPlayerList.error = null;
    });
    builder.addCase(getFullPlayerListData.fulfilled, (state, action) => {
      state.fullPlayerList.error = null;
      state.fullPlayerList.isFetching = false;
      state.fullPlayerList.data = action.payload;
    });
    builder.addCase(getFullPlayerListData.rejected, (state, action) => {
      state.fullPlayerList.isFetching = false;
      state.fullPlayerList.error = action.payload;
      state.fullPlayerList.data = null;
    });

    builder.addCase(getPlayerListDetailsData.pending, (state) => {
      state.playerDetails.isFetching = true;
      state.playerDetails.error = null;
    });
    builder.addCase(getPlayerListDetailsData.fulfilled, (state, action) => {
      state.playerDetails.error = null;
      state.playerDetails.isFetching = false;
      state.playerDetails.data = action.payload;
    });
    builder.addCase(getPlayerListDetailsData.rejected, (state, action) => {
      state.playerDetails.isFetching = false;
      state.playerDetails.error = action.payload;
      state.playerDetails.data = null;
    });

    builder.addCase(getFullPlayerListDetailsData.pending, (state) => {
      state.fullPlayerDetails.isFetching = true;
      state.fullPlayerDetails.error = null;
    });
    builder.addCase(getFullPlayerListDetailsData.fulfilled, (state, action) => {
      state.fullPlayerDetails.error = null;
      state.fullPlayerDetails.isFetching = false;
      state.fullPlayerDetails.data = action.payload;
    });
    builder.addCase(getFullPlayerListDetailsData.rejected, (state, action) => {
      state.fullPlayerDetails.isFetching = false;
      state.fullPlayerDetails.error = action.payload;
      state.fullPlayerDetails.data = null;
    });

    builder.addCase(getPlayerBalanceByIdData.pending, (state) => {
      state.playerBalance.isFetching = true;
      state.playerBalance.error = null;
    });
    builder.addCase(getPlayerBalanceByIdData.fulfilled, (state, action) => {
      state.playerBalance.error = null;
      state.playerBalance.isFetching = false;
      state.playerBalance.data = {
        ...action.payload,
        wallet: action.meta.arg.payload,
      };
    });
    builder.addCase(getPlayerBalanceByIdData.rejected, (state, action) => {
      state.playerBalance.isFetching = false;
      state.playerBalance.error = action.payload;
      state.playerBalance.data = null;
    });

    builder.addCase(transferToAgentData.pending, (state) => {
      state.transferToAgent.isFetching = true;
      state.transferToAgent.error = null;
    });
    builder.addCase(transferToAgentData.fulfilled, (state, action) => {
      state.transferToAgent.error = null;
      state.transferToAgent.isFetching = false;
      state.transferToAgent.data = action.payload;
    });
    builder.addCase(transferToAgentData.rejected, (state, action) => {
      state.transferToAgent.isFetching = false;
      state.transferToAgent.error = action.payload;
      state.transferToAgent.data = null;
    });

    builder.addCase(transferAllToAgentData.pending, (state) => {
      state.transferAllToAgent.isFetching = true;
      state.transferAllToAgent.error = null;
    });
    builder.addCase(transferAllToAgentData.fulfilled, (state, action) => {
      state.transferAllToAgent.error = null;
      state.transferAllToAgent.isFetching = false;
      state.transferAllToAgent.data = action.payload;
    });
    builder.addCase(transferAllToAgentData.rejected, (state, action) => {
      state.transferAllToAgent.isFetching = false;
      state.transferAllToAgent.error = action.payload;
      state.transferAllToAgent.data = null;
    });

    builder.addCase(getPlayersOptionsData.pending, (state ) => {
      state.playersOptions.isFetching = true;
      state.playersOptions.error = null;
    });
    builder.addCase(getPlayersOptionsData.fulfilled, (state , action) => {
      state.playersOptions.error = null;
      state.playersOptions.isFetching = false;
      state.playersOptions.data = action.payload;
    });
    builder.addCase(getPlayersOptionsData.rejected, (state , action) => {
      state.playersOptions.isFetching = false;
      state.playersOptions.error = action.payload;
      state.playersOptions.data = null;
    });

    builder.addCase(getPlayerWalletIdentitiesData.pending, (state ) => {
      state.playerWalletIdentities.isFetching = true;
      state.playerWalletIdentities.error = null;
    });
    builder.addCase(getPlayerWalletIdentitiesData.fulfilled, (state , action) => {
      state.playerWalletIdentities.error = null;
      state.playerWalletIdentities.isFetching = false;
      state.playerWalletIdentities.data = action.payload;
    });
    builder.addCase(getPlayerWalletIdentitiesData.rejected, (state , action) => {
      state.playerWalletIdentities.isFetching = false;
      state.playerWalletIdentities.error = action.payload;
      state.playerWalletIdentities.data = null;
    });

    builder.addCase(transferToPlayerData.pending, (state) => {
      state.transferToPlayer.isFetching = true;
      state.transferToPlayer.error = null;
    });
    builder.addCase(transferToPlayerData.fulfilled, (state, action) => {
      state.transferToPlayer.error = null;
      state.transferToPlayer.isFetching = false;
      state.transferToPlayer.data = action.payload;
    });
    builder.addCase(transferToPlayerData.rejected, (state, action) => {
      state.transferToPlayer.isFetching = false;
      state.transferToPlayer.error = action.payload;
      state.transferToPlayer.data = null;
    });

    builder.addCase(getAgentProfileData.pending, (state) => {
      state.agentProfile.isFetching = true;
      state.agentProfile.error = null;
    });
    builder.addCase(getAgentProfileData.fulfilled, (state, action) => {
      state.agentProfile.error = null;
      state.agentProfile.isFetching = false;
      state.agentProfile.data = action.payload;
    });
    builder.addCase(getAgentProfileData.rejected, (state, action) => {
      state.agentProfile.isFetching = false;
      state.agentProfile.error = action.payload;
      state.agentProfile.data = null;
    });

    builder.addCase(updateAgentProfileData.pending, (state) => {
      state.updateAgentProfile.isFetching = true;
      state.updateAgentProfile.error = null;
    });
    builder.addCase(updateAgentProfileData.fulfilled, (state, action) => {
      state.updateAgentProfile.error = null;
      state.updateAgentProfile.isFetching = false;
      state.updateAgentProfile.data = action.payload;
    });
    builder.addCase(updateAgentProfileData.rejected, (state, action) => {
      state.updateAgentProfile.isFetching = false;
      state.updateAgentProfile.error = action.payload;
      state.updateAgentProfile.data = null;
    });

    builder.addCase(getPlayerProfileData.pending, (state) => {
      state.playerProfile.isFetching = true;
      state.playerProfile.error = null;
    });
    builder.addCase(getPlayerProfileData.fulfilled, (state, action) => {
      state.playerProfile.error = null;
      state.playerProfile.isFetching = false;
      state.playerProfile.data = action.payload;
    });
    builder.addCase(getPlayerProfileData.rejected, (state, action) => {
      state.playerProfile.isFetching = false;
      state.playerProfile.error = action.payload;
      state.playerProfile.data = null;
    });

    builder.addCase(getExternalPlayerProfileData.pending, (state) => {
      state.externalPlayerProfile.isFetching = true;
      state.externalPlayerProfile.error = null;
    });
    builder.addCase(getExternalPlayerProfileData.fulfilled, (state, action) => {
      state.externalPlayerProfile.error = null;
      state.externalPlayerProfile.isFetching = false;
      state.externalPlayerProfile.data = action.payload;
    });
    builder.addCase(getExternalPlayerProfileData.rejected, (state, action) => {
      state.externalPlayerProfile.isFetching = false;
      state.externalPlayerProfile.error = action.payload;
      state.externalPlayerProfile.data = null;
    });

    builder.addCase(updateAgentRoleData.pending, (state) => {
      state.updateAgentRole.isFetching = true;
      state.updateAgentRole.error = null;
    });
    builder.addCase(updateAgentRoleData.fulfilled, (state, action) => {
      state.updateAgentRole.error = null;
      state.updateAgentRole.isFetching = false;
      state.updateAgentRole.data = action.payload;
    });
    builder.addCase(updateAgentRoleData.rejected, (state, action) => {
      state.updateAgentRole.isFetching = false;
      state.updateAgentRole.error = action.payload;
      state.updateAgentRole.data = null;
    });

    builder.addCase(getFilterRolesData.pending, (state) => {
      state.filterRoles.isFetching = true;
      state.filterRoles.error = null;
    });
    builder.addCase(getFilterRolesData.fulfilled, (state, action) => {
      state.filterRoles.error = null;
      state.filterRoles.isFetching = false;
      state.filterRoles.data = action.payload;
    });
    builder.addCase(getFilterRolesData.rejected, (state, action) => {
      state.filterRoles.isFetching = false;
      state.filterRoles.error = action.payload;
      state.filterRoles.data = null;
    });

    builder.addCase(getFilterRolesAllData.pending, (state) => {
      state.filterRolesAll.isFetching = true;
      state.filterRolesAll.error = null;
    });
    builder.addCase(getFilterRolesAllData.fulfilled, (state, action) => {
      state.filterRolesAll.error = null;
      state.filterRolesAll.isFetching = false;
      state.filterRolesAll.data = action.payload;
    });
    builder.addCase(getFilterRolesAllData.rejected, (state, action) => {
      state.filterRolesAll.isFetching = false;
      state.filterRolesAll.error = action.payload;
      state.filterRolesAll.data = null;
    });

    builder.addCase(toggleBlockAgentData.pending, (state) => {
      state.blockAgent.isFetching = true;
      state.blockAgent.error = null;
    });
    builder.addCase(toggleBlockAgentData.fulfilled, (state, action) => {
      state.blockAgent.error = null;
      state.blockAgent.isFetching = false;
      state.blockAgent.data = action.payload;
    });
    builder.addCase(toggleBlockAgentData.rejected, (state, action) => {
      state.blockAgent.isFetching = false;
      state.blockAgent.error = action.payload;
      state.blockAgent.data = null;
    });

    builder.addCase(setAgentNewPasswordData.pending, (state) => {
      state.agentNewPassword.isFetching = true;
      state.agentNewPassword.error = null;
    });
    builder.addCase(setAgentNewPasswordData.fulfilled, (state, action) => {
      state.agentNewPassword.error = null;
      state.agentNewPassword.isFetching = false;
      state.agentNewPassword.data = action.payload;
    });
    builder.addCase(setAgentNewPasswordData.rejected, (state, action) => {
      state.agentNewPassword.isFetching = false;
      state.agentNewPassword.error = action.payload;
      state.agentNewPassword.data = null;
    });

    builder.addCase(getBetListFiltersData.pending, (state) => {
      state.betListFilters.isFetching = true;
      state.betListFilters.error = null;
    });
    builder.addCase(getBetListFiltersData.fulfilled, (state, action) => {
      state.betListFilters.error = null;
      state.betListFilters.isFetching = false;
      state.betListFilters.data = action.payload;
    });
    builder.addCase(getBetListFiltersData.rejected, (state, action) => {
      state.betListFilters.isFetching = false;
      state.betListFilters.error = action.payload;
      state.betListFilters.data = null;
    });

    builder.addCase(getCasinoBetListFiltersData.pending, (state) => {
      state.casinoBetListFilters.isFetching = true;
      state.casinoBetListFilters.error = null;
    });
    builder.addCase(getCasinoBetListFiltersData.fulfilled, (state, action) => {
      state.casinoBetListFilters.error = null;
      state.casinoBetListFilters.isFetching = false;
      state.casinoBetListFilters.data = action.payload;
    });
    builder.addCase(getCasinoBetListFiltersData.rejected, (state, action) => {
      state.casinoBetListFilters.isFetching = false;
      state.casinoBetListFilters.error = action.payload;
      state.casinoBetListFilters.data = null;
    });

    builder.addCase(toggleBlockPlayerData.pending, (state) => {
      state.blockPlayer.isFetching = true;
      state.blockPlayer.error = null;
    });
    builder.addCase(toggleBlockPlayerData.fulfilled, (state, action) => {
      state.blockPlayer.error = null;
      state.blockPlayer.isFetching = false;
      state.blockPlayer.data = action.payload;
    });
    builder.addCase(toggleBlockPlayerData.rejected, (state, action) => {
      state.blockPlayer.isFetching = false;
      state.blockPlayer.error = action.payload;
      state.blockPlayer.data = null;
    });

    builder.addCase(getPlayerStatusData.pending, (state) => {
      state.playerStatus.isFetching = true;
      state.playerStatus.error = null;
    });
    builder.addCase(getPlayerStatusData.fulfilled, (state, action) => {
      state.playerStatus.error = null;
      state.playerStatus.isFetching = false;
      state.playerStatus.data = action.payload;
    });
    builder.addCase(getPlayerStatusData.rejected, (state, action) => {
      state.playerStatus.isFetching = false;
      state.playerStatus.error = action.payload;
      state.playerStatus.data = null;
    });

    builder.addCase(setPlayerNewPasswordData.pending, (state) => {
      state.playerNewPassword.isFetching = true;
      state.playerNewPassword.error = null;
    });
    builder.addCase(setPlayerNewPasswordData.fulfilled, (state, action) => {
      state.playerNewPassword.error = null;
      state.playerNewPassword.isFetching = false;
      state.playerNewPassword.data = action.payload;
    });
    builder.addCase(setPlayerNewPasswordData.rejected, (state, action) => {
      state.playerNewPassword.isFetching = false;
      state.playerNewPassword.error = action.payload;
      state.playerNewPassword.data = null;
    });

    builder.addCase(createPlayerData.pending, (state) => {
      state.createPlayer.isFetching = true;
      state.createPlayer.error = null;
    });
    builder.addCase(createPlayerData.fulfilled, (state, action) => {
      state.createPlayer.error = null;
      state.createPlayer.isFetching = false;
      state.createPlayer.data = action.payload;
    });
    builder.addCase(createPlayerData.rejected, (state, action) => {
      state.createPlayer.isFetching = false;
      state.createPlayer.error = action.payload;
      state.createPlayer.data = null;
    });

    builder.addCase(getCashierWalletIdentitiesData.pending, (state) => {
      state.cashierWalletIdentities.isFetching = true;
      state.cashierWalletIdentities.error = null;
    });
    builder.addCase(getCashierWalletIdentitiesData.fulfilled, (state, action) => {
      state.cashierWalletIdentities.error = null;
      state.cashierWalletIdentities.isFetching = false;
      state.cashierWalletIdentities.data = action.payload;
    });
    builder.addCase(getCashierWalletIdentitiesData.rejected, (state, action) => {
      state.cashierWalletIdentities.isFetching = false;
      state.cashierWalletIdentities.error = action.payload;
      state.cashierWalletIdentities.data = null;
    });

    builder.addCase(getCashierAdminOptionsData.pending, (state ) => {
      state.cashierAdminOptions.isFetching = true;
      state.cashierAdminOptions.error = null;
    });
    builder.addCase(getCashierAdminOptionsData.fulfilled, (state , action) => {
      state.cashierAdminOptions.error = null;
      state.cashierAdminOptions.isFetching = false;
      state.cashierAdminOptions.data = action.payload;
    });
    builder.addCase(getCashierAdminOptionsData.rejected, (state , action) => {
      state.cashierAdminOptions.isFetching = false;
      state.cashierAdminOptions.error = action.payload;
      state.cashierAdminOptions.data = null;
    });
  },
});

export const {
  clearListState,
  clearFullListState,
  clearDetailsState,
  clearBrandsListState,
  clearWalletsState,
  clearCreateWalletsState,
  clearDescendantAgentsState,
  clearAgentRolesState,
  clearWalletIdentitiesState,
  clearCreateAgentState,
  clearEnableWalletState,
  clearDisableWalletState,
  clearAddWalletState,
  clearAgentBalanceState,
  clearPlayerListState,
  clearPlayerBalanceState,
  clearPlayerListDetailsState,
  clearTransferToAgentState,
  clearPlayersOptionsState,
  clearPlayerWalletIdentitiesState,
  clearTransferToPlayerState,
  clearFullDetailsState,
  clearFullAgentsOptionsState,
  clearTransferAllToAgentState,
  clearFullTrAgentsOptionsState,
  clearFullPlayerListState,
  clearFullPlayerListDetailsState,
  clearAgentProfileState,
  clearUpdateAgentProfileState,
  clearPlayerProfileState,
  clearExternalPlayerProfileState,
  clearAgentRoleState,
  clearToggleBlockAgentState,
  clearAgentNewPasswordState,
  clearToggleBlockPlayerState,
  clearPlayerStatusState,
  clearPlayerNewPasswordState,
  clearCreatePlayerState,
  clearAdminBalanceState,
  clearCashierWalletIdentitiesState,
  clearCashierAdminOptionsState,
} = agentsSlice.actions;

export default agentsSlice.reducer;
